import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import EditorSection from "components/EditorSection";
import DashboardInfoTableV2 from "components/DashboardInfoTableV2/ui/DashboardInfoTableV2";
import { LOAD_TAB_SETTINGS, SET_CURRENT_TABLE_CELL, UPDATE_TABLE } from "store/constans";
import { setCurrentTableStateAC, ElementsAction, loadCustomProperties } from "store/actions/table";
import { addTranslationsToState } from "store/actions/translations";
import { setCrossTabNamespace, MERGE_SYNCED_STATE, registerExternalWindow, unregisterExternalWindow } from "utils/crossTabSync";

const getDataRowId = (row, idx) => {
  const ids =
    (Array.isArray(row?.lineElements) && row.lineElements) ||
    (Array.isArray(row?.original?.lineElements) && row.original.lineElements) ||
    [];
  if (ids.length) return `le:${ids.join("|")}`;
  // Must match react-table getRowId fallback used in TableBase (string index)
  return String(idx);
};

const buildRowSelectionBySelectedRows = (activeTab, selectedRows) => {
  if (!Array.isArray(activeTab?.rowsData) || !Array.isArray(selectedRows)) return {};
  const set = new Set(selectedRows);
  const res = {};
  activeTab.rowsData.forEach((row, idx) => {
    const ids = Array.isArray(row?.lineElements) ? row.lineElements : [];
    if (ids.some((id) => set.has(id))) {
      const key = getDataRowId(row, idx);
      res[key] = true;
    }
  });
  return res;
};

const remapIndexRowSelectionToStable = (activeTab, indexRowSelection) => {
  if (!indexRowSelection) return {};
  const keys = Object.keys(indexRowSelection);
  if (!keys.length) return {};

  if (!keys.every((k) => /^\d+$/.test(k))) return indexRowSelection;
  const res = {};
  (activeTab?.rowsData || []).forEach((row, idx) => {
    if (indexRowSelection[String(idx)]) {
      const key = getDataRowId(row, idx);
      res[key] = true;
    }
  });
  return res;
};

const buildElementIdsByRowSelection = (activeTab, rowSelection) => {
  if (!activeTab?.rowsData || !rowSelection) return [];
  const keys = rowSelection && typeof rowSelection === "object" ? rowSelection : {};
  const ids = [];
  (activeTab.rowsData || []).forEach((row, idx) => {
    const key = getDataRowId(row, idx);
    if (!keys[key]) return;
    const lineEls = Array.isArray(row?.lineElements) ? row.lineElements : [];
    lineEls.forEach((id) => ids.push(id));
  });
  return Array.from(new Set(ids));
};

const TableWindowPage = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const search = location.search;
  
  const storageKey = (() => {
    try {
      return new URLSearchParams(search).get("key");
    } catch {
      return null;
    }
  })();

  const [tableState, setTableState] = useState(() => {
    if (!storageKey) return null;
    try {
      const raw = localStorage.getItem(storageKey);
      const parsed = raw ? JSON.parse(raw) : null;
      return parsed?.activeTab ?? null;
    } catch {
      return null;
    }
  });

  const [bootstrap, setBootstrap] = useState(() => {
    if (!storageKey) return null;
    try {
      const raw = localStorage.getItem(storageKey);
      const parsed = raw ? JSON.parse(raw) : null;
      return parsed?.bootstrap ?? null;
    } catch {
      return null;
    }
  });

  const tabs = useSelector((state) => state.table?.tabs || []);
  const modelIdForTab = bootstrap?.project?.idToOpen || null;
  const reportTab = tabs.find((tab) => {
    if (tab.id !== 1 || tab.type !== "report") return false;
    if (modelIdForTab) {
      return tab.modelId === modelIdForTab;
    }
    return !tab.modelId;
  });

  useEffect(() => {
    window.__IS_EXTERNAL_TABLE_WINDOW__ = true;
    document.body.classList.add("table-window-mode");
    return () => {
      document.body.classList.remove("table-window-mode");
      try { delete window.__IS_EXTERNAL_TABLE_WINDOW__; } catch {}
    };
  }, []);

  useEffect(() => {
    if (!storageKey) return;
    try {
      const raw = localStorage.getItem(storageKey);
      const parsed = raw ? JSON.parse(raw) : null;
      const modelId = parsed?.bootstrap?.project?.idToOpen || null;
      
      if (modelId) {
        registerExternalWindow(modelId);
        setCrossTabNamespace(modelId);
      }
      
      return () => {
        if (modelId) {
          unregisterExternalWindow(modelId);
        }
      };
    } catch {}
  }, [storageKey]);

  useEffect(() => {
    if (!storageKey) return;
    try {
      const raw = localStorage.getItem(storageKey);
      const parsed = raw ? JSON.parse(raw) : null;

      const bs = parsed?.bootstrap;
      const activeTab = parsed?.activeTab;

      if (activeTab && (!tableState || tableState.id !== activeTab.id)) {
        setTableState(activeTab);
      }
      setBootstrap(bs || null);

      const tabId = activeTab?.id;
      const tabSettings = bs?.tabsSettings?.[tabId];
      if (tabId && tabSettings) {
        const { columnSchemes, ...colorScheme } = tabSettings;
        dispatch({
          type: LOAD_TAB_SETTINGS,
          payload: { currentTableId: tabId, colorScheme, columnSchemes },
        });
      }
      if (bs?.translations) {
        dispatch(addTranslationsToState(bs.translations));
      }
      if (bs?.condition) {
        dispatch({
          type: MERGE_SYNCED_STATE,
          payload: { condition: bs.condition },
          meta: { source: 'local', reason: 'bootstrap_condition' },
        });
      }
      if (bs?.viewerTemplateSettings) {
        dispatch({
          type: MERGE_SYNCED_STATE,
          payload: { viewer: { templateSettings: bs.viewerTemplateSettings } },
          meta: { source: 'local', reason: 'bootstrap_templateSettings' },
        });
      }
      // Load customPropertiesValues from bootstrap if available
      if (bs?.viewer?.customPropertiesValues) {
        dispatch({
          type: MERGE_SYNCED_STATE,
          payload: { viewer: { customPropertiesValues: bs.viewer.customPropertiesValues } },
          meta: { source: 'local', reason: 'bootstrap_customPropertiesValues' },
        });
      }
      if (bs?.viewer?.customProperties) {
        dispatch({
          type: MERGE_SYNCED_STATE,
          payload: { viewer: { customProperties: bs.viewer.customProperties } },
          meta: { source: 'local', reason: 'bootstrap_customProperties' },
        });
      }
      
      // Load custom properties from API if not in bootstrap or if bootstrap data is empty
      const hasCustomPropertiesInBootstrap = bs?.viewer?.customPropertiesValues?.length > 0;
      const modelIdForBootstrap = bs?.project?.idToOpen || null;
      
      if (!hasCustomPropertiesInBootstrap && modelIdForBootstrap) {
        // Load from API with modelId to ensure proper initialization
        dispatch(loadCustomProperties(modelIdForBootstrap));
      } else if (!hasCustomPropertiesInBootstrap) {
        // Fallback: try without modelId (may fail but that's ok)
        dispatch(loadCustomProperties());
      }

      const cs = bs?.currentTableState || {};
      let nextCS = { ...cs };
      let rowSelectionFromStorage = {};
      if (modelIdForBootstrap) {
        try {
          const { getCurrentNamespace } = require('utils/crossTabSync');
          const currentNs = getCurrentNamespace() || modelIdForBootstrap;
          const rowSelectionKey = currentNs ? `rowSelection:${currentNs}` : `rowSelection:${modelIdForBootstrap}`;
          const raw = localStorage.getItem(rowSelectionKey);
          if (raw) {
            const parsed = JSON.parse(raw);
            if (parsed && typeof parsed === "object") {
              rowSelectionFromStorage = parsed;
            }
          }
        } catch {}
      }

      const hasRS = cs.rowSelection && Object.keys(cs.rowSelection).length;
      const hasSR = Array.isArray(cs.selectedRows) && cs.selectedRows.length;
      const hasStorageRS = rowSelectionFromStorage && Object.keys(rowSelectionFromStorage).length;

      if (hasStorageRS) {
        nextCS.rowSelection = remapIndexRowSelectionToStable(activeTab, rowSelectionFromStorage);
      } else if (hasRS) {
        nextCS.rowSelection = remapIndexRowSelectionToStable(activeTab, cs.rowSelection);
      } else if (hasSR) {
        nextCS.rowSelection = buildRowSelectionBySelectedRows(activeTab, cs.selectedRows);
      } else {
        nextCS.rowSelection = {};
      }

      dispatch(setCurrentTableStateAC(nextCS));

      try {
        if (modelIdForBootstrap && tabId) {
          const elementIds = buildElementIdsByRowSelection(activeTab, nextCS.rowSelection);
          const ctrlKey = `table-ctrl:${modelIdForBootstrap}:${tabId}`;
          const msg = {
            v: 1,
            ts: Date.now(),
            rid: Math.random().toString(36).slice(2),
            from: "ext-bootstrap",
            event: "selection",
            payload: { elementIds },
          };
          localStorage.setItem(ctrlKey, JSON.stringify(msg));
        }
      } catch {}
    } catch { }
  }, [dispatch, storageKey]);

  // Listen to UPDATE_TABLE from Redux store and update tableState when table is refreshed
  useEffect(() => {
    if (!reportTab || !tableState) return;
    if (tableState.id !== 1 && tableState.type !== "report") return;
    
    setTableState((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        rowsData: reportTab.rowsData || prev.rowsData || [],
        columnsInfo: reportTab.columnsInfo || prev.columnsInfo || [],
        properties: reportTab.properties !== undefined ? reportTab.properties : prev.properties,
        groups: reportTab.groups !== undefined ? reportTab.groups : prev.groups,
      };
    });
  }, [reportTab, tableState?.id]);

  useEffect(() => {
    const modelIdForStorage = bootstrap?.project?.idToOpen || null;
    if (!modelIdForStorage) return;
    
    const onStorage = (e) => {
      if (e.storageArea !== localStorage || !e.newValue) return;
      
      try {
        const { getCurrentNamespace } = require('utils/crossTabSync');
        const currentNs = getCurrentNamespace() || modelIdForStorage;
        const rowSelectionKey = currentNs ? `rowSelection:${currentNs}` : `rowSelection:${modelIdForStorage}`;
        if (e.key === rowSelectionKey) {
          try {
            const raw = localStorage.getItem(rowSelectionKey);
            const parsed = raw ? JSON.parse(raw) : {};
            if (parsed && typeof parsed === "object") {
              dispatch(setCurrentTableStateAC({ rowSelection: parsed }));
            }
          } catch {}
          return;
        }
      } catch {}
      
      if (e.key === "bs:tab-actions") {
        try {
          const msg = JSON.parse(e.newValue);
          const a = msg?.action;
          if (!a) return;
          if (a.type === SET_CURRENT_TABLE_CELL) {
            const { columnId, lineElements, newValue } = a.payload || {};
            if (!lineElements || !lineElements.length) return;
            setTableState((prev) => {
              if (!prev || !prev.rowsData) return prev;
              const rowsData = prev.rowsData.map((row) => {
                const match = (row?.lineElements || []).some((id) =>
                  lineElements.includes(id)
                );
                return match ? { ...row, [columnId]: newValue } : row;
              });
              return { ...prev, rowsData };
            });
          } else if (a.type === "UPDATE_TABLE") {
            const updatedTable = a.payload || {};
            if (tableState?.id === 1 || tableState?.type === "report") {
              setTableState((prev) => {
                if (!prev) return prev;
                return {
                  ...prev,
                  rowsData: Array.isArray(updatedTable.rowsData) ? updatedTable.rowsData : (prev.rowsData || []),
                  columnsInfo: Array.isArray(updatedTable.columnsInfo) ? updatedTable.columnsInfo : (prev.columnsInfo || []),
                  properties: updatedTable.properties !== undefined ? updatedTable.properties : prev.properties,
                  groups: updatedTable.groups !== undefined ? updatedTable.groups : prev.groups,
                };
              });
            }
          } else if (a.type === "LOAD_CUSTOM_PROPERTIES") {
            // Custom properties are already handled by crossTabSync middleware
            // This is just for reference
          }
        } catch { }
        return;
      }
      if (e.key?.startsWith("table-ctrl:")) {
        try {
          const msg = JSON.parse(e.newValue);
          const elementIds = msg?.payload?.elementIds || [];
          dispatch(ElementsAction(elementIds.length ? elementIds : null));
        } catch { }
        return;
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [dispatch, tableState?.id, bootstrap?.project?.idToOpen]);

  useEffect(() => {
    const modelId = bootstrap?.project?.idToOpen || null;
    const tabId = tableState?.id || null;
    if (!tabId || !modelId) return;

    const detachCountKey = `table-detached-count:${modelId}:${tabId}`;
    const openCountKey = `model-external-open:${modelId}`;
    const showSignalKey = `host:show-table:${modelId}`;

    const getCount = (k) => {
      try {
        const val = localStorage.getItem(k);
        const n = parseInt(val ?? "0", 10);
        return Number.isFinite(n) ? n : 0;
      } catch {
        return 0;
      }
    };

    const decKey = (k) => {
      const n = Math.max(0, getCount(k) - 1);
      try {
        if (n > 0) localStorage.setItem(k, String(n));
        else localStorage.removeItem(k);
      } catch {}
    };

    let finalized = false;
    const finalizeAndCleanup = () => {
      if (!finalized) finalized = true;
      try { decKey(openCountKey); } catch {}
      try { decKey(detachCountKey); } catch {}
      // notify host to re-read rowSelection from localStorage on close
      try { localStorage.setItem("newTabOpened", String(Date.now())); } catch {}
      try { localStorage.setItem(showSignalKey, String(Date.now())); } catch {}
      try { if (storageKey) localStorage.removeItem(storageKey); } catch {}
    };

    const onPageHide = () => finalizeAndCleanup();
    const onBeforeUnload = () => finalizeAndCleanup();
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("beforeunload", onBeforeUnload);
      finalizeAndCleanup();
    };
  }, [tableState?.id, bootstrap?.project?.idToOpen, storageKey]);

  if (!tableState) {
    return (
      <div style={{ padding: 24 }}>
        Failed to load table data. Close the window and try again.
      </div>
    );
  }

  const dummyTableForEditor = { tabs: [] };
  const hiddenHostStyle = {
    position: "fixed",
    width: 1,
    height: 1,
    opacity: 0,
    pointerEvents: "none",
    bottom: 0,
    right: 0,
    zIndex: -1,
    overflow: "hidden",
  };

  return (
    <div
      className="table-window-layout"
      style={{ height: "100vh", display: "flex", flexDirection: "column" }}
    >
      <div style={hiddenHostStyle}>
        <EditorSection
          autodesk={window.Autodesk}
          projectData={bootstrap?.project || null}
          quickCounterData={null}
          table={dummyTableForEditor}
          sidebarIsOpened={false}
        />
      </div>
      <div style={{ flex: "1 1 auto", minHeight: 240, overflow: "hidden" }}>
        <DashboardInfoTableV2
          table={tableState}
          withOutActions={false}
          isExternalWindow={true}
          projectData={bootstrap?.project || null}
          modelId={bootstrap?.project?.idToOpen || null}
        />
      </div>
    </div>
  );
};

export default TableWindowPage;